import type { PresenceStatus } from '@livechat/shared';
import { useEffect, useRef, useState } from 'react';

const OPTIONS: { label: string; value: PresenceStatus }[] = [
	{ label: 'Available', value: 'available' },
	{ label: 'Busy', value: 'busy' },
	{ label: 'Offline', value: 'offline' },
];

export function StatusPicker({
	onChange,
	value,
}: {
	onChange: (status: PresenceStatus) => void;
	value: PresenceStatus;
}) {
	const [open, setOpen] = useState(false);
	const wrapper = useRef<HTMLDivElement>(null);
	const selected = OPTIONS.find((option) => option.value === value) ?? OPTIONS[2]!;

	useEffect(() => {
		if (!open) return;
		const close = (event: PointerEvent) => {
			if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('pointerdown', close);
		document.addEventListener('keydown', closeOnEscape);
		return () => {
			document.removeEventListener('pointerdown', close);
			document.removeEventListener('keydown', closeOnEscape);
		};
	}, [open]);

	return (
		<div className={`status-picker presence-${value}`} ref={wrapper}>
			<button
				aria-expanded={open}
				aria-haspopup="listbox"
				aria-label={`Status: ${selected.label}`}
				className="status-picker-trigger"
				onClick={() => setOpen((current) => !current)}
				type="button"
			>
				<span className="presence-dot" />
				<span>{selected.label}</span>
				<svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
					<path d="m4 6 4 4 4-4" />
				</svg>
			</button>
			{open && (
				<div aria-label="Status" className="status-picker-menu" role="listbox">
					{OPTIONS.map((option) => (
						<button
							aria-selected={option.value === value}
							className={`presence-${option.value}`}
							key={option.value}
							onClick={() => {
								setOpen(false);
								if (option.value !== value) onChange(option.value);
							}}
							role="option"
							type="button"
						>
							<span className="presence-dot" />
							<span>{option.label}</span>
							{option.value === value && <span aria-hidden="true">✓</span>}
						</button>
					))}
				</div>
			)}
		</div>
	);
}
